import React, { useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import axios from 'axios';
import Pagination from '../components/Pagination';
import './SoatExecutionLogs.css';

const SoatExecutionLogs = () => {
  const { user, token } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    status: '',
    startDate: ''
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/automation/soat-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error al cargar logs de ejecución de SOAT:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);



  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const filteredLogs = logs.filter(log => {
    let matches = true;
    if (filters.status && log.status !== filters.status) {
      matches = false;
    }
    if (filters.startDate) {
      const logDate = new Date(log.created_at).toISOString().split('T')[0];
      if (logDate !== filters.startDate) {
        matches = false;
      }
    }
    return matches;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const getDuration = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const diff = e.getTime() - s.getTime();
    if (isNaN(diff)) return '—';
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Redirigir a welcome si no es superadmin
  if (user && user.role !== 'superadmin') {
    return <Navigate to="/welcome" />;
  }

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="📋 Registro de Ejecuciones del Bot SOAT" />

        <div className="page-content">
          {/* Tarjetas de Métricas Rápidas */}
          <div className="metrics-grid">
            <div className="metric-card card-total">
              <span className="metric-label">Total Ejecuciones</span>
              <h2>{logs.length}</h2>
            </div>
            <div className="metric-card card-success">
              <span className="metric-label">Exitosas</span>
              <h2>{logs.filter(l => l.status === 'success').length}</h2>
            </div>
            <div className="metric-card card-failed">
              <span className="metric-label">Fallidas</span>
              <h2>{logs.filter(l => l.status === 'failed').length}</h2>
            </div>
          </div>

          {/* Sección de Filtros */}
          <div className="filters-bar" style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="filter-group">
              <label>📅 Fecha:</label>
              <input 
                type="date" 
                name="startDate" 
                value={filters.startDate} 
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>🚦 Estado:</label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">Todos</option>
                <option value="success">Éxito</option>
                <option value="failed">Fallo</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setFilters({ status: '', startDate: '' })} className="btn-secondary">
                🔄 Restablecer
              </button>
              <button onClick={fetchLogs} className="btn-primary">
                🔄 Recargar
              </button>
            </div>
          </div>

          <div className="table-section">
            <div className="table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Duración</th>
                    <th>Origen / Tipo</th>
                    <th>Revisados</th>
                    <th>Aprobados</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                        Cargando registros de ejecución...
                      </td>
                    </tr>
                  ) : currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                        No se encontraron registros de ejecución.
                      </td>
                    </tr>
                  ) : (
                    currentItems.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.start_time).toLocaleString('es-CO')}</td>
                        <td>{new Date(log.end_time).toLocaleString('es-CO')}</td>
                        <td>{getDuration(log.start_time, log.end_time)}</td>
                        <td>
                          {log.scheduled_time ? (
                            <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                              🤖 Automático ({new Date(log.scheduled_time).getHours() === 2 ? '2 AM' : '6 AM'})
                            </span>
                          ) : (
                            <span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                              👤 Manual
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{log.checked_count}</td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: log.updated_count > 0 ? '#10b981' : 'var(--text-secondary)' }}>
                          {log.updated_count}
                        </td>
                        <td>
                          <span className={`status ${log.status === 'success' ? 'paid' : 'expired'}`} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
                            {log.status === 'success' ? 'Éxito' : 'Fallo'}
                          </span>
                        </td>
                        <td>
                          {log.status === 'failed' && (
                            <button 
                              onClick={() => setSelectedLog(log)} 
                              className="btn-primary" 
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              🔍 Ver Error
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <Pagination 
                currentPage={currentPage}
                totalItems={filteredLogs.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal para detalles del error */}
      {selectedLog && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: 'var(--card-bg, #ffffff)', padding: '25px', borderRadius: '12px', maxWidth: '600px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: '15px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ❌ Detalle del Error de Ejecución
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontWeight: '600', marginBottom: '5px', color: 'var(--text-primary)' }}>Ejecución del:</p>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>{new Date(selectedLog.start_time).toLocaleString('es-CO')}</p>
              
              <p style={{ fontWeight: '600', marginBottom: '5px', color: 'var(--text-primary)' }}>Mensaje de Error:</p>
              <pre style={{ background: 'var(--input-bg, #f3f4f6)', color: 'var(--text-primary)', padding: '15px', borderRadius: '6px', overflowX: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '13px', border: '1px solid var(--border-color, #e5e7eb)' }}>
                {selectedLog.error_message}
              </pre>

              {selectedLog.next_retry_at && (
                <div style={{ marginTop: '15px', padding: '12px', background: '#eff6ff', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
                  <p style={{ color: '#1e3a8a', fontSize: '13px', fontWeight: '500', margin: 0 }}>
                    🔄 Reintento automático programado para las: <strong>{new Date(selectedLog.next_retry_at).toLocaleString('es-CO')}</strong>
                  </p>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedLog(null)} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoatExecutionLogs;
